import * as React from "react";
import { CircularProgress } from "@mui/material";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";

type HealthCheckComponentProps = {
    apiUrl: string
}

type HealthCheckStatus = "Healthy" | "UnHealthy";

type HealthCheckResponse = {
    Status:  HealthCheckStatus,
    HealthChecks: {
        Components: string,
        Status:  HealthCheckStatus,
        Description: string|null
    }[]
}

export type ApiCallState<TResponse> = {
    loading: boolean;
    error: Error | null;
    data: TResponse | null;
    refresh?: () => void | undefined;
};

async function apiFetch<TResponse>(apiUrl: string, options: RequestInit): Promise<TResponse> {
    const response = await fetch(`${apiUrl}/health`, options);
    if (response.ok) {
        const result = await response.json();
        return result as TResponse;
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

class HttpError extends Error {
    status: number;
    constructor(status: number, message?: string) {
        super(message);
        this.status = status;
    }
}

function useApiHealthCheck(apiUrl: string) {
    const [state, setState] = React.useState<ApiCallState<HealthCheckResponse>>({
        data: null,
        error: null,
        loading: false,
    });
    const [refreshIndex, setRefreshIndex] = React.useState<number>(0);

    React.useEffect(() => {
        setState({ data: null as any, error: null as any, loading: true });
        (async () => {
            try {
                const data = await apiFetch<HealthCheckResponse>(apiUrl, {});
                setState({ data, error: null, loading: false });
            } catch (error) {
                setState({ data: null, error, loading: false });
            }
        })();
    }, [apiUrl, refreshIndex]);

    return [
        state.loading as boolean,
        state.error as Error | null,
        state.data as HealthCheckResponse | null,
        () => { setRefreshIndex((x: number) => ++x) }
    ] as const;
}


export const HealthCheck : React.FC<HealthCheckComponentProps> = ({ apiUrl }) => {
    apiUrl = apiUrl.replace(/\/$/, "");
    const [ loading, error, data, refresh ] = useApiHealthCheck(apiUrl);


    React.useEffect(() => {
        if (!loading)
            console.log('URL', apiUrl, 'Data:', data, 'Error:', error);
    }, [loading, data, error, apiUrl]);

    return (
        <span style={{display: "table-row"}}>
            {loading &&
                <CircularProgress size={"1rem"} aria-busy="true" />
            }
            {(error || (data && data!.Status !== "Healthy")) &&
                <SentimentVeryDissatisfiedIcon htmlColor="red" aria-label="unhealthy-icon" aria-hidden="false" onClick={() => refresh()} />
            }
            {data && (data!.Status === "Healthy") &&
                <SentimentVerySatisfiedIcon htmlColor="green" aria-label="healthy-icon" aria-hidden="false" onClick={() => refresh()} />
            }
        </span>
    );
}